import Jsona from 'jsona';
const dataFormatter = new Jsona();

/**
 * jsona links related resources by reference, so a document whose `included` resources point back at
 * their parents (e.g. onboarding profile -> badges -> onboarding profile) yields a circular object graph.
 * Redux-persist cannot JSON.stringify such a slice and silently stops saving it.
 *
 * Only true cycles are cut: a reference to an object that is an ancestor on the current traversal path
 * is replaced with `{ id, type }`, the same shape jsona builds for a relationship that is not included.
 * Shared but acyclic references are preserved and acyclic payloads are left untouched.
 * Ported from fieldIQ-mobile app/rtk/baseQuery.ts (FIQ-953), which sets the reference to undefined.
 */
const breakCircularRefs = root => {
  if (root === null || typeof root !== 'object') return root;
  const onPath = new Set();
  const done = new Set();

  const visit = node => {
    onPath.add(node);
    const keys = Array.isArray(node) ? node.map((_value, index) => index) : Object.keys(node);
    for (const key of keys) {
      const child = node[key];
      if (child === null || typeof child !== 'object') continue;
      if (onPath.has(child)) {
        // Back-reference to an ancestor: break the cycle.
        node[key] = child.id !== undefined && child.type ? { id: child.id, type: child.type } : undefined;
      } else if (!done.has(child)) {
        visit(child);
      }
    }
    onPath.delete(node);
    done.add(node);
  };

  visit(root);
  return root;
};

export { breakCircularRefs, dataFormatter };
